import HeadingWrapper from "@components/common/HeadingWrapper";
import {
  DSBadge,
  DSCardWrapper,
  DSLinkButton,
} from "@components/design-system/molecules";
import { OpenPipButton } from "@components/pip/OpenPipButton";
import {
  faCloudDownload,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { useCurrentUser } from "@hooks/use-current-user";
import { Tooltip } from "@mui/material";
import { Link } from "react-router";

import { ExportNoteInline } from "./ExportNoteInline";
import PimsProviderSetting, {
  PIMS_PROVIDER_OPTIONS,
} from "../account/PimsProviderSetting";

function isChrome() {
  const ua = navigator.userAgent;
  return /Chrome/.test(ua) && !/Edg|OPR/.test(ua);
}

function isSafari() {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome/.test(ua);
}

function PimspalCard({ isPrimary }) {
  return (
    <DSCardWrapper variant={isPrimary ? "success" : "info"}>
      {isPrimary && (
        <DSBadge
          variant="green"
          size="sm"
          dataTestId="recommended-badge-pimspal"
        >
          Recommended for your PIMS
        </DSBadge>
      )}
      <Tooltip
        arrow
        placement="right"
        title="PIMSPal currently only supports cloud-based PIMS like ezyVet, Pulse, Vetspire, etc."
      >
        <span>
          <DSBadge size="sm" icon={faInfoCircle}>
            For Cloud-Based PIMS
          </DSBadge>
        </span>
      </Tooltip>
      <p className="my-sm text-sm text-secondary">
        {isPrimary
          ? "PIMSPal brings your notes right into your PIMS. Install it once and export with one click."
          : "For a more auto-magic experience, download our PIMSPal extension!"}
      </p>
      {isChrome() ? (
        <DSLinkButton
          icon={faCloudDownload}
          href="https://chromewebstore.google.com/detail/scribenote-pimspal/ibenkaeanlddcgpbfokocbhgbpfhieoc"
          target="_blank"
        >
          Download PIMSPal for Google Chrome
        </DSLinkButton>
      ) : (
        <p className="text-xs text-placeholder">
          PIMSPal is available for Google Chrome only.
        </p>
      )}
    </DSCardWrapper>
  );
}

function PimspalUnsupportedCard() {
  return (
    <DSCardWrapper variant="warning">
      <DSBadge size="sm" icon={faExclamationTriangle}>
        PIMSPal Unavailable
      </DSBadge>
      <p className="my-sm text-sm text-secondary">
        PIMSPal requires Google Chrome. For the best export
        experience, try using{" "}
        <a
          href="https://www.google.com/chrome/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline hover:text-brand"
        >
          Google Chrome
        </a>
        .
      </p>
    </DSCardWrapper>
  );
}

function isWidgetModeSupported() {
  return !isSafari() && !!window.documentPictureInPicture;
}

function WidgetModeCard({ isPrimary, noteUuid }) {
  if (!isWidgetModeSupported()) return null;

  return (
    <DSCardWrapper variant={isPrimary ? "success" : "info"}>
      {isPrimary && (
        <DSBadge
          variant="green"
          size="sm"
          dataTestId="recommended-badge-widget"
        >
          Recommended for your PIMS
        </DSBadge>
      )}
      <Tooltip
        arrow
        placement="right"
        title="Widget Mode supports all PIMS — drag and drop your note into any application."
      >
        <span>
          <DSBadge size="sm" icon={faInfoCircle}>
            For Cloud-Based and Server-Based PIMS
          </DSBadge>
        </span>
      </Tooltip>
      <p className="my-sm text-sm text-secondary">
        {isPrimary
          ? "Widget Mode lets you easily drag and drop this note into your PIMS. No install required!"
          : "Widget Mode helps you easily drag and drop this note into your PIMS."}
      </p>
      <OpenPipButton
        pipUrl={`/pip/note/${noteUuid}`}
        isBadgeEnabled={true}
      />
    </DSCardWrapper>
  );
}

function WidgetModeUnsupportedCard() {
  return (
    <DSCardWrapper variant="warning">
      <DSBadge size="sm" icon={faExclamationTriangle}>
        Widget Mode Unavailable
      </DSBadge>
      <p className="my-sm text-sm text-secondary">
        Widget Mode is not supported in your current browser. For the
        best export experience, try using{" "}
        <a
          href="https://www.google.com/chrome/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline hover:text-brand"
        >
          Google Chrome
        </a>
        .
      </p>
    </DSCardWrapper>
  );
}

export default function ExportNoteSection({
  note,
  getValues,
  exportExpanded,
  onEmailClick,
  onExportSuccess,
}) {
  const { user } = useCurrentUser();

  const pimsProvider = user?.pimsProvider || "";

  const pimsOption = PIMS_PROVIDER_OPTIONS.find(
    (o) => o.value === pimsProvider,
  );
  const pimsType = pimsOption?.pimsType ?? "unknown";
  const isCloud = pimsType === "cloud";
  const isDesktop = pimsType === "desktop";

  // Determine primary export method
  // Cloud PIMS → PIMSpal primary (if Chrome), widget secondary
  // Desktop PIMS → Widget primary, PIMSpal not shown
  // Other/none → show both equally
  const pimspalIsPrimary = isCloud;
  const widgetIsPrimary = isDesktop;

  return (
    <div className="flex flex-col gap-md">
      {!pimsProvider && (
        <div className="flex flex-col gap-md">
          <DSCardWrapper
            variant="ghost"
            dataTestId="pims-selection-prompt"
          >
            <h4 className="text-sm font-semibold text-primary">
              Which PIMS do you use?
            </h4>
            <p className="text-xs text-secondary mt-xs">
              Select your practice management software so we can
              recommend the best export method.
            </p>
            <div className="mt-sm max-w-xs">
              <PimsProviderSetting />
            </div>
            <p className="mt-sm text-xs text-placeholder">
              You can always change this later in{" "}
              <Link
                to="/account?tab=pims"
                className="text-brand underline hover:text-brand"
              >
                Account Settings
              </Link>
              .
            </p>
          </DSCardWrapper>
        </div>
      )}
      {/* Primary recommendation */}
      {/* Need to keep this mounted so companion can open widget mode even if you havent selected a pims provider yet */}
      <div
        className={`flex flex-row gap-md ${!pimsProvider && "hidden"}`}
      >
        {pimspalIsPrimary ? (
          isChrome() ? (
            <>
              <PimspalCard isPrimary />
              <WidgetModeCard
                isPrimary={false}
                noteUuid={note?.uuid}
              />
            </>
          ) : (
            <>
              <PimspalUnsupportedCard />
              <WidgetModeCard
                isPrimary={false}
                noteUuid={note?.uuid}
              />
            </>
          )
        ) : widgetIsPrimary ? (
          isWidgetModeSupported() ? (
            <WidgetModeCard isPrimary noteUuid={note?.uuid} />
          ) : (
            <WidgetModeUnsupportedCard />
          )
        ) : (
          <>
            <WidgetModeCard isPrimary={false} noteUuid={note?.uuid} />
            <PimspalCard isPrimary={false} />
          </>
        )}
      </div>
      {/* Other export options */}
      <HeadingWrapper heading={<h4>Other Export Options</h4>} />
      <ExportNoteInline
        getValues={getValues}
        exportExpanded={exportExpanded}
        note={note}
        onEmailClick={onEmailClick}
        onExportSuccess={onExportSuccess}
      />
    </div>
  );
}
